<!-- Profile edit layer: a dark band docked under the photo, spanning its full width.
     "across": widens from the clicked icon (startInsets) to full width, so the icon slides left.
     "down": slides down out of the photo. Content (icon, prefix, inputs) comes from the slot; the
     "actions" slot (Settings: EditButtons) goes on its own row underneath. Its top is at the
     field; when its bottom would be cut off by the dialog's visible area (the email editor with
     several addresses), it moves up just enough, never above that area's top (2026-10-16). -->

<template>
  <Transition :name="`edit-layer-${direction}`">
    <div
      v-if="isOpen"
      ref="layerRef"
      class="edit-layer open-field"
      :class="direction"
      :style="[startInsets, {top: `${-offset}px`}]">
      <slot />
      <div v-if="$slots.actions" class="actions"><slot name="actions" /></div>
    </div>
  </Transition>
</template>

<script setup>
import {nextTick, ref, watch} from "vue";
import {useEventListener, useResizeObserver} from "@vueuse/core";

const props = defineProps({
  isOpen: {type: Boolean, default: false},
  direction: {type: String, default: "across"},
  startInsets: {type: Object, default: () => ({})} // {"--start-left": "Npx", "--start-right"}
});

const MARGIN = 8; // between the layer and the edge of the visible area

// the nearest ancestor that clips (the dialog's scrolling body), else the window
function getVisibleArea(element) {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(node).overflowY)) {
      return node.getBoundingClientRect();
    }
  }
  return {top: 0, bottom: window.innerHeight};
}

// how far up the layer must move so its bottom shows, within the room above it
function getOffset(layer, currentOffset) {
  const rect = layer.getBoundingClientRect();
  const area = getVisibleArea(layer);
  const naturalTop = rect.top + currentOffset;
  const overflow = rect.bottom + currentOffset + MARGIN - area.bottom;
  const room = naturalTop - area.top - MARGIN;
  return Math.max(0, Math.min(overflow, room));
}

const layerRef = ref(null);
const offset = ref(0);
const updateOffset = () => {
  if (layerRef.value) offset.value = getOffset(layerRef.value, offset.value);
};
watch(() => props.isOpen, async (isOpen) => {
  offset.value = 0;
  if (!isOpen) return;
  await nextTick();
  updateOffset();
});
useResizeObserver(layerRef, updateOffset); // an address added or removed
useEventListener(window, "resize", updateOffset);
</script>

<style scoped>
.edit-layer {
  --pad-x: 12px;
  --pad-bottom: 6px;
  position: absolute; /* in .meta-wrapper, directly under the photo */
  top: 0;
  left: 0;
  right: 0;
  z-index: 11; /* over the "?" popups (HelpPopup: 10), which a pointer left there can open */
  display: flex;
  flex-wrap: wrap; /* the actions take a row of their own */
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px var(--pad-x) var(--pad-bottom); /* same space before the icon as after the input */
  box-sizing: border-box;
  background: #333;
  color: white;
  border-radius: 6px; /* less round than the dialog's 12px; the footer shares the lower corners */
  /* lifted off the dialog, without a backdrop: a close and a wide shadow, and a fine light edge */
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08), /* light catching the top edge */
    0 0 0 1px rgba(0, 0, 0, 0.3), /* a dark hairline just outside: a crisp edge on the grey */
    0 0 14px rgba(0, 0, 0, 0.3), /* all round, the top too */
    0 10px 28px rgba(0, 0, 0, 0.4); /* below: lifted */
  overflow: hidden; /* clips the content while the layer opens, and the footer's corners */
}
/* "down" holds a FieldPair (labels over inputs): 10px from the edges to the label's lettering
   (the 15px line box adds 3px above it) and to the inputs, and 10px between the two fields */
.edit-layer.down {
  --pad-x: 10px;
  --pad-bottom: 10px;
  gap: 10px;
  padding: 7px var(--pad-x) var(--pad-bottom);
}
/* the footer (EditButtons): edge to edge and down to the layer's bottom, under a hairline */
.actions {
  flex-basis: calc(100% + 2 * var(--pad-x));
  margin: 4px calc(-1 * var(--pad-x)) calc(-1 * var(--pad-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.edit-layer-across-enter-active,
.edit-layer-across-leave-active {
  transition: left 180ms ease-out, right 180ms ease-out;
}
.edit-layer-across-enter-from,
.edit-layer-across-leave-to {
  left: var(--start-left, 0);
  right: var(--start-right, 0);
}
.edit-layer-down-enter-active,
.edit-layer-down-leave-active {
  transition: clip-path 180ms ease-out;
}
.edit-layer-down-enter-from,
.edit-layer-down-leave-to {
  clip-path: inset(0 0 100% 0);
}
@media (prefers-reduced-motion: reduce) {
  .edit-layer-across-enter-active,
  .edit-layer-across-leave-active,
  .edit-layer-down-enter-active,
  .edit-layer-down-leave-active {
    transition: none;
  }
}
</style>
